import { PricingLogicAlgorithmEntry } from "@app/shared/models/enrichment-detail.model";

/** Bangkok is UTC+7 with no DST — a fixed offset is safe. */
const ICT_OFFSET_MS = 7 * 60 * 60 * 1000;

export const PLA_PACKAGE_TYPES = ["InternetX2", "InternetX3"] as const;

/** Legacy CMS contract — the old screen showed "n/12". */
export const MAX_PLA_PERIODS = 12;

/** One editable period row. Dates are ICT calendar dates ("YYYY-MM-DD"). */
export interface PeriodRow {
  /** BE-assigned entry id; absent for rows added this session (BE stamps). */
  id?: string;
  startDate: string;
  endDate: string;
}

function dateParts(date: string): [number, number, number] {
  const [y, m, d] = date.replaceAll("/", "-").split("-").map(Number);
  return [y, m, d];
}

/** "YYYY-MM-DD" (ICT calendar date) → ISO instant of 00:00:00 ICT that day.
 *  e.g. "2025-12-09" → "2025-12-08T17:00:00Z" (matches batch-bot rows). */
export function ictDateToStartISO(date: string): string {
  const [y, m, d] = dateParts(date);
  return new Date(Date.UTC(y, m - 1, d) - ICT_OFFSET_MS)
    .toISOString()
    .replace(".000Z", "Z");
}

/** "YYYY-MM-DD" → ISO instant of 23:59:59 ICT that day.
 *  e.g. "2025-12-14" → "2025-12-14T16:59:59Z". */
export function ictDateToEndISO(date: string): string {
  const [y, m, d] = dateParts(date);
  return new Date(Date.UTC(y, m - 1, d, 23, 59, 59) - ICT_OFFSET_MS)
    .toISOString()
    .replace(".000Z", "Z");
}

/** ISO instant → ICT calendar date "YYYY-MM-DD" (inverse of the two above). */
export function isoToICTDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (!iso || Number.isNaN(t)) return "";
  return new Date(t + ICT_OFFSET_MS).toISOString().slice(0, 10);
}

/** Normalize "YYYY-MM-DD" | "YYYY/MM/DD" to the dash form. */
function normDate(date: string): string {
  return date.replaceAll("/", "-").slice(0, 10);
}

/** Calendar-date arithmetic: "YYYY-MM-DD" + n days (pure UTC math). */
function addDays(date: string, days: number): string {
  const [y, m, d] = dateParts(date);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Latest-ending row above `index` that has an endDate, or null. */
function latestPriorEnd(
  rows: readonly PeriodRow[],
  index: number,
): { index: number; endDate: string } | null {
  let latest: { index: number; endDate: string } | null = null;
  for (let i = 0; i < index; i++) {
    const end = rows[i].endDate ? normDate(rows[i].endDate) : "";
    if (end && (!latest || end > latest.endDate)) latest = { index: i, endDate: end };
  }
  return latest;
}

/** Minimum allowed startDate ("YYYY-MM-DD") for the row at `index`:
 *  max(today, latest prior endDate + 1 day). Rows already persisted by the
 *  BE (they have an id) are exempt → null. FE-only rule — stricter than the
 *  BE's validatePLA (per STARTDATE-MIN-SPEC.md). */
export function minNewRowStartDate(
  rows: readonly PeriodRow[],
  index: number,
  today: string,
): string | null {
  const row = rows[index];
  if (!row || row.id) return null;
  const prior = latestPriorEnd(rows, index);
  const afterPrior = prior ? addDays(prior.endDate, 1) : "";
  const t = normDate(today);
  return afterPrior > t ? afterPrior : t;
}

export type PeriodStatus = "Expired" | "Active" | "Pending";

/** Status chip for a stored window, evaluated against `now`. */
export function periodStatus(
  startISO: string | undefined,
  endISO: string | undefined,
  now: Date,
): PeriodStatus {
  const start = startISO ? new Date(startISO).getTime() : Number.NaN;
  const end = endISO ? new Date(endISO).getTime() : Number.NaN;
  if (!Number.isNaN(end) && end < now.getTime()) return "Expired";
  if (!Number.isNaN(start) && start > now.getTime()) return "Pending";
  return "Active";
}

/** Validate the period rows against the Net X2/X3 rules. Returns
 *  human-readable errors — empty array = valid. Mirrors the BE's
 *  validatePLA, plus FE-only minimum-start rules for rows added this
 *  session (no id yet): they must not start before `today` (ICT calendar
 *  date) nor on/before a prior row's end. */
export function validatePeriodRows(rows: readonly PeriodRow[], today: string): string[] {
  const errors: string[] = [];
  if (rows.length > MAX_PLA_PERIODS) {
    errors.push(`At most ${MAX_PLA_PERIODS} periods are allowed.`);
  }
  const windows: { i: number; start: number; end: number }[] = [];
  rows.forEach((row, i) => {
    if (!row.startDate || !row.endDate) {
      errors.push(`Period ${i + 1}: start and end dates are required.`);
      return;
    }
    const start = new Date(ictDateToStartISO(row.startDate)).getTime();
    const end = new Date(ictDateToEndISO(row.endDate)).getTime();
    if (end < start) {
      errors.push(`Period ${i + 1}: end date must not be before start date.`);
      return;
    }
    const min = minNewRowStartDate(rows, i, today);
    if (min && normDate(row.startDate) < min) {
      const prior = latestPriorEnd(rows, i);
      errors.push(
        prior && min > normDate(today)
          ? `Period ${i + 1}: start date must be after period ${prior.index + 1}'s end date.`
          : `Period ${i + 1}: start date must not be in the past.`,
      );
      return;
    }
    for (const w of windows) {
      if (start <= w.end && w.start <= end) {
        errors.push(`Period ${i + 1} overlaps period ${w.i + 1}.`);
        break;
      }
    }
    windows.push({ i, start, end });
  });
  return errors;
}

/** Build the wire entries from form state. Every row shares the single
 *  header (popId → plaSpecId, packageType → name, packageName →
 *  description); existing ids are preserved, new rows omit `id` so the
 *  BE stamps a nanoid. */
export function rowsToEntries(
  popId: string,
  packageType: string,
  packageName: string,
  rows: readonly PeriodRow[],
): PricingLogicAlgorithmEntry[] {
  return rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    name: packageType,
    description: packageName,
    plaSpecId: popId,
    validFor: {
      startDateTime: ictDateToStartISO(r.startDate),
      endDateTime: ictDateToEndISO(r.endDate),
    },
  }));
}

/** Hydrate form state from stored entries: header from the first entry,
 *  one row per entry. Legacy mixed-header data collapses to the first
 *  entry's header and self-heals on the next save (per spec). */
export function entriesToForm(
  entries: readonly PricingLogicAlgorithmEntry[] | undefined,
): { popId: string; packageType: string; packageName: string; rows: PeriodRow[] } {
  const first = entries?.[0];
  return {
    popId: first?.plaSpecId ?? "",
    packageType: first?.name ?? "",
    packageName: first?.description ?? "",
    rows: (entries ?? []).map((e) => ({
      ...(e.id ? { id: e.id } : {}),
      startDate: isoToICTDate(e.validFor?.startDateTime ?? ""),
      endDate: isoToICTDate(e.validFor?.endDateTime ?? ""),
    })),
  };
}
